import { useState } from 'react';
import { useMutation } from 'urql';
import { api } from '@/lib/api';
import { getErrorMessage } from '@/lib/error-message';
import { UpdateCrmIntegrationMutation } from './graphql';
import type { CaspioConfig } from './caspio-config';
import type { PlatformConfig } from './platform-config';

export function useCrmIntegrationActions(organizationId: string, integrationId: string) {
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [, runUpdateCrmIntegration] = useMutation(UpdateCrmIntegrationMutation);

  const save = async (name: string, config: CaspioConfig | PlatformConfig) => {
    setError(null);
    setIsSaving(true);

    const result = await runUpdateCrmIntegration({
      id: integrationId,
      name: name.trim(),
      config,
      updatedAt: new Date().toISOString(),
    });

    setIsSaving(false);

    if (result.error) setError(getErrorMessage(result.error, 'Failed to save the CRM settings'));
  };

  const remove = async () => {
    setError(null);
    setIsRemoving(true);

    const { error: apiError } = await api.organizations({ organizationId })['crm-integration'].delete();

    setIsRemoving(false);

    if (apiError) setError(getErrorMessage(apiError, 'Failed to remove the CRM integration'));
  };

  return { error, isSaving, isRemoving, save, remove };
}
